import { Component } from '@theme/component';

/**
 * Barre d'achat collante.
 *
 * Elle ne gère aucune logique de panier ni de variante : elle pilote les
 * commandes natives d'Horizon (le sélecteur de variantes et le bouton d'ajout
 * du bloc produit). Tout ce qui est prix, disponibilité, URL et contenu du
 * panier reste donc la responsabilité du thème, et la barre ne peut pas
 * diverger de la fiche produit.
 *
 * @extends {Component<{ bar: HTMLElement, price: HTMLElement, select?: HTMLSelectElement, button: HTMLButtonElement }>}
 */
class LrsStickyAtc extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {MutationObserver | undefined} */
  #priceMirror;
  /** @type {MutationObserver | undefined} */
  #sizeMirror;
  /** @type {HTMLButtonElement | null} */
  #nativeButton = null;

  connectedCallback() {
    super.connectedCallback();
    // Le bloc produit peut être rendu après la barre : on attend une frame.
    requestAnimationFrame(() => this.#connectToProductBlock());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#priceMirror?.disconnect();
    this.#sizeMirror?.disconnect();
  }

  /** Bouton d'ajout natif du bloc produit, jamais celui de la barre. */
  get #nativeAddToCart() {
    return /** @type {HTMLButtonElement | null} */ (
      document.querySelector('.buy-buttons-block [ref="addToCartButton"]')
    );
  }

  /** Conteneur de prix natif, jamais celui de la barre. */
  get #nativePrice() {
    return document.querySelector('.product-details [ref="priceContainer"]');
  }

  #connectToProductBlock() {
    this.#nativeButton = this.#nativeAddToCart;
    if (!this.#nativeButton) return;

    this.#watchNativeButton(this.#nativeButton);
    this.#mirrorPrice();
    this.#syncAvailability();
    this.#mirrorSize();
  }

  /**
   * Recopie la taille choisie. La source reste le sélecteur de variantes natif :
   * la barre n'a aucune logique de variante à elle, elle ne peut donc pas
   * afficher autre chose que ce que le client a réellement sélectionné.
   */
  #mirrorSize() {
    const target = this.refs.size;
    if (!target) return;

    const wanted = (this.dataset.optionName || '').trim().toLowerCase();
    const picker = document.querySelector('variant-picker');
    if (!picker) return;

    const read = () => {
      const checked = Array.from(picker.querySelectorAll('input:checked'));
      let chosen = checked[checked.length - 1];

      if (wanted) {
        const match = checked.find((input) => {
          const own = (input.dataset.optionName || '').trim().toLowerCase();
          if (own) return own.includes(wanted);
          const group = input.closest('fieldset, .variant-option');
          const legend = group?.querySelector('legend, .variant-option__label');
          return legend?.textContent.trim().toLowerCase().includes(wanted);
        });
        if (match) chosen = match;
      }

      if (!chosen) {
        target.textContent = '';
        return;
      }

      const label = picker.querySelector(`label[for="${CSS.escape(chosen.id)}"]`);
      const value = (label?.textContent || chosen.value || '').trim();

      // Le nom de l'option n'est pas présumé : « Taille » sur un t-shirt,
      // « Diamètre » sur un set de pins. On reprend celui que le sélecteur
      // natif porte sur l'entrée cochée, jamais un intitulé écrit en dur.
      const optionName = (chosen.dataset.optionName || '').trim();
      if (!value) {
        target.textContent = '';
        return;
      }
      target.textContent = optionName ? `${optionName} ${value}` : value;
    };

    read();
    picker.addEventListener('change', read);
    this.#sizeMirror = new MutationObserver(read);
    this.#sizeMirror.observe(picker, { childList: true, subtree: true });
  }

  /**
   * La barre suit la visibilité du bouton natif, pas la position de défilement :
   * elle reste juste quelle que soit la hauteur de la fiche ou du média.
   * @param {HTMLElement} button
   */
  #watchNativeButton(button) {
    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      this.refs.bar?.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
    });

    this.#observer.observe(button);
  }

  /**
   * Recopie le prix natif à chaque changement de variante. Horizon re-rend le
   * bloc produit par la Section Rendering API ; observer le DOM évite de
   * dépendre d'un évènement que le thème n'émet pas.
   */
  #mirrorPrice() {
    const source = this.#nativePrice;
    const target = this.refs.price;
    if (!source || !target) return;

    const copy = () => {
      target.innerHTML = source.innerHTML;
      this.#syncAvailability();
    };

    this.#priceMirror = new MutationObserver(copy);
    this.#priceMirror.observe(source, { childList: true, subtree: true, characterData: true });
    copy();
  }

  /** Aligne l'état du bouton de la barre sur celui du bouton natif. */
  #syncAvailability() {
    const native = this.#nativeAddToCart;
    const button = this.refs.button;
    if (!native || !button) return;
    button.disabled = native.disabled;
  }

  /**
   * Répercute le choix de taille sur le sélecteur de variantes natif : c'est lui
   * qui met à jour prix, disponibilité et variante soumise au panier.
   */
  selectOption() {
    const select = this.refs.select;
    if (!select) return;

    const valueId = select.value;
    const control = document.querySelector(
      `variant-picker [data-option-value-id="${CSS.escape(valueId)}"]`
    );
    if (!control) return;

    if (control instanceof HTMLOptionElement) {
      const parent = control.closest('select');
      if (!parent) return;
      parent.value = control.value;
      parent.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (control instanceof HTMLInputElement) {
      if (control.disabled) return;
      control.checked = true;
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Délègue l'ajout au panier au bouton natif, même mécanique que la barre
   * d'Horizon : aucune requête panier n'est dupliquée ici.
   */
  addToCart() {
    const native = this.#nativeAddToCart;
    if (!native || native.disabled) return;
    native.dataset.puppet = 'true';
    native.click();
  }
}

if (!customElements.get('lrs-sticky-atc')) {
  customElements.define('lrs-sticky-atc', LrsStickyAtc);
}
